import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, CheckCircle2, Package, Printer } from 'lucide-react'
import { ScanAgentBadge } from '@/components/features/worker/ScanAgentBadge'
import { ScanAgentConfigPanel } from '@/components/features/worker/ScanAgentConfigPanel'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useBoxScan, useBoxScanMutations } from '@/hooks/useBoxScan'
import { useBoxScanWebSocket } from '@/hooks/useBoxScanWebSocket'
import { isLocalPrintAgentOnline, useLocalPrintAgent } from '@/hooks/useLocalPrintAgent'
import { extractApiError } from '@/lib/api'
import { printApi, resolveAgentId } from '@/lib/api/print'
import { formatDuplicateConflictMessage, formatDuplicateLocation, formatScanDuplicateMessage } from '@/lib/utils/scanDuplicate'
import { severityForScanError } from '@/types/api-errors'
import type { TaskBoxRow } from '@/types'
import { useToast } from '@/hooks/useToast'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface ScanWorkPanelProps {
  taskUuid: string
  taskId: string
  palletNum: number
  boxes: TaskBoxRow[]
  workerName: string
  onBack: () => void
  onRelease: () => void
  isReleasing: boolean
}

export function ScanWorkPanel({
  taskUuid,
  taskId,
  palletNum,
  boxes,
  workerName,
  onBack,
  onRelease,
  isReleasing,
}: ScanWorkPanelProps) {
  const { toast } = useToast()
  const [activeBoxNum, setActiveBoxNum] = useState<number | null>(() => boxes[0]?.num ?? null)
  const inputRef = useRef<HTMLInputElement>(null)

  const wsConnected = useBoxScanWebSocket(taskUuid, activeBoxNum ?? undefined, activeBoxNum != null)
  const { data: boxView, isLoading } = useBoxScan(
    taskUuid,
    activeBoxNum ?? undefined,
    activeBoxNum != null,
    wsConnected,
  )
  const { openBox, scan } = useBoxScanMutations(taskUuid, activeBoxNum ?? 0)
  const { data: localAgent } = useLocalPrintAgent()
  const agentOnline = isLocalPrintAgentOnline(localAgent)
  const openKeyRef = useRef('')
  const closedNoticeRef = useRef('')
  const printedAdvanceRef = useRef('')
  const qc = useQueryClient()
  const retryPrint = useMutation({
    mutationFn: async (boxNum: number) => {
      const agentId = await resolveAgentId({ localOnly: true })
      await printApi.printBox(taskUuid, boxNum, { agent_id: agentId, worker_name: workerName })
    },
    onSuccess: () => {
      toast.success('Задание на печать отправлено')
      void qc.invalidateQueries({ queryKey: ['task-boxes', taskUuid] })
      void qc.invalidateQueries({ queryKey: ['task-pallets', taskUuid] })
      if (activeBoxNum != null) {
        void qc.invalidateQueries({ queryKey: ['task-scan-box', taskUuid, activeBoxNum] })
      }
    },
    onError: err => {
      toast.error(extractApiError(err))
    },
  })

  useEffect(() => {
    openKeyRef.current = ''
    closedNoticeRef.current = ''
    printedAdvanceRef.current = ''
  }, [activeBoxNum, taskUuid])

	const advanceAfterPrint = (closedNum: number) => {
    const next = boxes.find(b => b.num > closedNum)
    setActiveBoxNum(next?.num ?? null)
  }

  const isPrinting = retryPrint.isPending || (
    boxView?.status === 'closed' &&
    (boxView.print_status === 'queued' || boxView.print_status === 'processing')
  )

  useEffect(() => {
    if (activeBoxNum == null || boxView?.status !== 'closed') return
    const key = `${taskUuid}:${activeBoxNum}:closed`
    if (closedNoticeRef.current === key) return
    closedNoticeRef.current = key
    toast.success(`Коробка №${activeBoxNum} закрыта`)
  }, [boxView?.status, activeBoxNum, taskUuid, toast])

  useEffect(() => {
    if (activeBoxNum == null || boxView?.status !== 'closed' || !boxView?.printed) return
    const key = `${taskUuid}:${activeBoxNum}:printed`
    if (printedAdvanceRef.current === key) return
    printedAdvanceRef.current = key
    toast.success(`КА коробки №${activeBoxNum} напечатана`)
    advanceAfterPrint(activeBoxNum)
  }, [activeBoxNum, boxView?.status, boxView?.printed, taskUuid, toast, boxes])

  useEffect(() => {
    if (activeBoxNum == null || isLoading) return
    if (boxView?.status === 'closed') return
    const key = `${taskUuid}:${activeBoxNum}`
    if (openKeyRef.current === key) return
    openKeyRef.current = key
    openBox.mutate(
      { workerName, agentId: localAgent?.agent_id },
      {
      onError: e => {
        openKeyRef.current = ''
        toast.error(extractApiError(e))
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per box
  }, [activeBoxNum, taskUuid, boxView?.status, isLoading, workerName, localAgent?.agent_id])

  useEffect(() => {
    if (boxView?.status === 'open') {
      inputRef.current?.focus()
    }
  }, [boxView?.status, activeBoxNum])

  const handleScan = async (raw: string) => {
    const code = raw.trim()
    if (!code || activeBoxNum == null) return
    try {
      const res = await scan.mutateAsync({ code, workerName, agentId: localAgent?.agent_id })
      if (res.data.item.accepted) {
        if (res.data.item.validation_status === 'duplicate') {
          toast.warning(
            formatDuplicateConflictMessage({
              raw: code,
              task_id: taskId,
              dup_box_num: res.data.item.duplicate_of?.box_num,
              dup_pallet_num: res.data.item.duplicate_of?.pallet_num,
            }),
          )
        } else {
          toast.success(
            res.data.item.validation_status === 'pending'
              ? `Код принят · валидация (${res.data.item.pg_count}/${res.data.item.items_per_box} в PG)`
              : `Код принят (${res.data.item.pg_count}/${res.data.item.items_per_box})`,
          )
        }
      }
    } catch (err) {
      const msg = formatScanDuplicateMessage(err)
      const anyErr = err as { response?: { data?: { code?: string } } }
      const sev = severityForScanError(anyErr.response?.data?.code)
      if (sev === 'warning') toast.warning(msg)
      else toast.error(msg)
    }
  }

  const pgCount = boxView?.pg_count ?? 0
  const pendingCount = boxView?.pending_count ?? 0
  const target = boxView?.items_per_box ?? 0
  const scanSlotsFull = boxView?.status === 'open' && pgCount+pendingCount >= target
  const closingSoon = boxView?.status === 'open' && pgCount === target && pendingCount > 0
  const dupCount = boxView?.duplicate_count ?? 0
  const boxNumsOnPallet = useMemo(() => new Set(boxes.map(b => b.num)), [boxes])
  const highlightedSourceBoxes = useMemo(() => {
    const out = new Set<number>()
    if (!boxView?.codes) return out
    for (const c of boxView.codes) {
      if (c.status !== 'duplicate' || !c.dup_box_num) continue
      if (c.dup_pallet_num && c.dup_pallet_num !== palletNum) continue
      if (boxNumsOnPallet.has(c.dup_box_num)) out.add(c.dup_box_num)
    }
    return out
  }, [boxView?.codes, boxNumsOnPallet, palletNum])

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            К паллетам
          </Button>
          <h2 className="text-xl font-bold text-gray-900">Паллета №{palletNum}</h2>
        </div>
        <Button
          variant="secondary"
          onClick={onRelease}
          disabled={isReleasing || isPrinting}
          isLoading={isReleasing}
        >
          Освободить
        </Button>
        <ScanAgentBadge />
        {wsConnected && (
          <span className="text-xs text-green-600" title="Обновления коробки в реальном времени">
            live
          </span>
        )}
      </div>

      <ScanAgentConfigPanel taskUuid={taskUuid} boxNum={activeBoxNum} workerName={workerName} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
        {boxes.map(b => {
          const active = b.num === activeBoxNum
          const dupSource = highlightedSourceBoxes.has(b.num)
          return (
            <button
              key={b.num}
              type="button"
              disabled={isPrinting}
              onClick={() => setActiveBoxNum(b.num)}
              className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:opacity-50 ${
                active
                  ? 'border-blue-500 bg-blue-50'
                  : dupSource
                    ? 'border-red-400 bg-red-50 hover:border-red-500'
                    : b.printed
                      ? 'border-green-400 bg-green-50 hover:border-green-500'
                      : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className={`font-medium ${!active && b.printed && !dupSource ? 'text-green-800' : ''}`}>
                Коробка №{b.num}
              </span>
              {dupSource && <span className="block text-xs text-red-600">оригинал дубля</span>}
            </button>
          )
        })}
      </div>

      {activeBoxNum == null ? (
        <p className="text-gray-500 text-center py-8">Выберите коробку</p>
      ) : isLoading && !boxView ? (
        <LoadingSpinner className="mt-8" />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold">Коробка №{activeBoxNum}</h3>
            {boxView?.status === 'closed' && boxView?.printed && !retryPrint.isPending && (
              <CheckCircle2 className="w-5 h-5 text-green-600 ml-auto" />
            )}
            {isPrinting && (
              <span className="ml-auto inline-flex items-center gap-1 text-sm text-blue-600">
                <Printer className="w-4 h-4 animate-pulse" />
                Печать…
              </span>
            )}
          </div>

          {boxView?.status === 'closed' && !boxView?.printed && isPrinting && (
            <p className="text-sm text-blue-600 mb-4">
              Печать КА коробки №{activeBoxNum} — дождитесь завершения
            </p>
          )}

          {boxView?.status === 'closed' && boxView?.print_status === 'failed' && !retryPrint.isPending && (
            <div className="mb-4">
              <p className="text-sm text-red-600 mb-2">
                {boxView.print_error_message || 'Не удалось напечатать КА коробки'}
              </p>
              <Button
                size="sm"
                variant="secondary"
                disabled={!agentOnline}
                onClick={() => activeBoxNum != null && retryPrint.mutate(activeBoxNum)}
              >
                <Printer className="w-4 h-4 mr-1" />
                Повторить печать
              </Button>
            </div>
          )}

          <p className="text-3xl font-bold text-gray-900 mb-1">
            {pgCount}
            <span className="text-lg font-normal text-gray-400"> / {target}</span>
          </p>
          {closingSoon && (
            <p className="text-sm text-blue-600 mb-2">Завершение валидации — коробка закроется автоматически</p>
          )}
          {scanSlotsFull && pendingCount > 0 && !closingSoon && (
            <p className="text-sm text-amber-600 mb-2">
              Ожидайте валидацию ({pendingCount}) перед следующим сканом
            </p>
          )}
          {!scanSlotsFull && pendingCount > 0 && (
            <p className="text-sm text-amber-600 mb-2">Валидация: {pendingCount} код(ов)</p>
          )}
          {dupCount > 0 && (
            <p className="text-sm text-red-600 mb-2">Дубликатов в коробке: {dupCount}</p>
          )}

          {boxView?.status === 'open' && (
            <>
              <input
                ref={inputRef}
                type="text"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={scanSlotsFull ? 'Коробка заполнена — дождитесь валидации' : 'Отсканируйте код маркировки…'}
                disabled={scan.isPending || scanSlotsFull}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void handleScan((e.target as HTMLInputElement).value).then(() => {
                      ;(e.target as HTMLInputElement).value = ''
                    })
                  }
                }}
              />
            </>
          )}

          {boxView && boxView.codes.length > 0 && (
            <ul className="mt-4 max-h-48 overflow-y-auto text-xs space-y-1">
              {boxView.codes.map(c => {
                const isDup = c.status === 'duplicate'
                const dupHint = isDup
                  ? formatDuplicateLocation(c.dup_box_num, c.dup_pallet_num, taskId, c.raw)
                  : null
                return (
                  <li
                    key={c.canonical}
                    className={`truncate font-mono rounded px-2 py-1 ${
                      isDup ? 'text-red-700 bg-red-50 border border-red-200' : 'text-gray-600'
                    }`}
                  >
                    {c.raw}
                    {c.status === 'pending' && ' …'}
                    {dupHint && <span className="block text-red-600 normal-case font-sans">{dupHint}</span>}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
